import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import NumerologyLayout from './components/numerology/NumerologyLayout';

/** Legacy /numerology/learn/:slug → /numerology/study/:slug */
function RedirectNumerologyLearnSlugToStudy() {
  const { slug } = useParams();
  return <Navigate to={`/numerology/study/${slug}`} replace />;
}
import { supabase } from './lib/supabaseClient';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import RequireBirthData from './components/RequireBirthData';
import Login from './pages/Login';
import Register from './pages/Register';
import Welcome from './pages/Welcome';
import Dashboard from './pages/Dashboard';
import BirthData from './pages/BirthData';
import Charts from './pages/Charts';
import Charts2 from './pages/Charts2';
import Files from './pages/Files';
import Profile from './pages/Profile';
import ProfileSetup from './pages/ProfileSetup';
import Home from './pages/Home';
import Astrology from './pages/Astrology';
import AstrologyChart from './pages/AstrologyChart';
import ChartPage from './pages/ChartPage';
import ChartReportPage from './pages/ChartReportPage';
import ChartReportPrintPage from './pages/ChartReportPrintPage';
import Debug from './pages/Debug';
import WitchProfile from './components/WitchProfile/WitchProfile';
import Runes from './pages/Runes';
import Tarot from './pages/Tarot';
import Courses from './pages/Courses';
import RunesCourseIndex from './pages/RunesCourseIndex';
import RuneDeepDive from './pages/RuneDeepDive';
import TarotCoursePage from './pages/courses/TarotCoursePage';
import Palmistry101Page from './pages/courses/Palmistry101Page';
import NumerologyReportEnginePage from './pages/NumerologyReportEnginePage';
import NumerologyReportPrintPage from './pages/NumerologyReportPrintPage';
import NumerologyHubPage from './pages/numerology/NumerologyHubPage';
import NumerologyLearnLandingPage from './pages/numerology/NumerologyLearnLandingPage';
import NumerologySamplesPage from './pages/numerology/NumerologySamplesPage';
import NumerologyStudySlugPage from './components/numerology/NumerologyStudySlugPage';
import NumerologyLegacyAccessRedirect from './components/numerology/NumerologyLegacyAccessRedirect';
import Horoscope from './pages/Horoscope';
import NotFound from './pages/NotFound';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      console.error('App: Supabase client is not initialized. Check env variables.');
      setLoading(false);
      return;
    }

    let subscription = null;
    const timeoutId = setTimeout(() => setLoading(false), 5000);

    try {
      const res = supabase.auth.onAuthStateChange(
        async (_event, session) => {
          const currentUser = session?.user ?? null;
          setUser(currentUser);
          setLoading(false);
        }
      );
      
      subscription = res?.data?.subscription;

      supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
        if (sessionError && process.env.NODE_ENV !== 'production') {
          console.error('App: Session error (non-critical):', sessionError);
        }
        setUser(session?.user ?? null);
        setLoading(false);
      }).catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('App: Error fetching session:', error);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('App: Error setting up auth listener:', error);
      setLoading(false);
    }

    return () => {
      clearTimeout(timeoutId);
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('App: Error unsubscribing auth listener:', error);
        }
      }
    };
  }, []);

  const handleLogout = async () => {
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.error('App: Error signing out:', error);
      }
    }
  };

  // Fallback UI jos Supabase ei ole käytettävissä
  if (!supabase) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a0d2e',
        color: '#ffffff',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px' }}>
          <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>⚠️ Configuration Error</h1>
          <p style={{ marginBottom: '10px', fontSize: '18px' }}>
            Config error: missing Supabase env vars in Vercel Production. Redeploy required.
          </p>
          <p style={{ marginBottom: '20px', color: '#b0b0b0' }}>
            Required environment variables:
          </p>
          <ul style={{ textAlign: 'left', display: 'inline-block', marginBottom: '20px' }}>
            <li><code>REACT_APP_SUPABASE_URL</code></li>
            <li><code>REACT_APP_SUPABASE_ANON_KEY</code></li>
          </ul>
          <p style={{ color: '#b0b0b0', fontSize: '14px' }}>
            Configure these in Vercel project settings (Settings → Environment Variables) and redeploy.
          </p>
        </div>
      </div>
    );
  }

  const LoadingScreen = () => (
    <div className="loading" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="moon-loader">
        <div className="moon"></div>
      </div>
    </div>
  );

  const Wrap = ({ children }) => (loading ? <LoadingScreen /> : children);

  return (
    <Router>
      <AppShell
        user={user}
        onLogout={handleLogout}
        Wrap={Wrap}
      />
    </Router>
  );
}

const DEFAULT_TITLE = 'Astral Moon Magic | Astrologinen Palvelu';
const CHART_TITLE = 'Astral Moon Magic | Astrology Chart Reading';

function AppShell({ user, onLogout, Wrap }) {
  const location = useLocation();
  const hideNavbarForChart = location.pathname === '/chart' || location.pathname.startsWith('/chart/');
  const hideNavbarForNumerology =
    location.pathname === '/numerology' || location.pathname.startsWith('/numerology/');
  const hideNavbar = hideNavbarForChart || hideNavbarForNumerology;
  const isNumerologyRoute = hideNavbarForNumerology;
  const isChartRoute = location.pathname === '/chart' || location.pathname === '/chart/report' || location.pathname === '/chart/report/print';

  useEffect(() => {
    if (isChartRoute) {
      document.title = CHART_TITLE;
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'robots';
        document.head.appendChild(meta);
      }
      meta.content = 'noindex, nofollow';
    } else if (isNumerologyRoute) {
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'robots';
        document.head.appendChild(meta);
      }
      meta.content = 'noindex, nofollow';
    } else {
      const meta = document.querySelector('meta[name="robots"]');
      if (meta && meta.content === 'noindex, nofollow') {
        meta.remove();
      }
      document.title = DEFAULT_TITLE;
    }
  }, [isChartRoute, isNumerologyRoute]);

  return (
    <div className="App">
      {!hideNavbar && <Navbar user={user} onLogout={onLogout} />}
      <Routes>
        <Route path="/" element={<Wrap><Home /></Wrap>} />
        <Route path="/astrology" element={<Wrap><Astrology /></Wrap>} />
        <Route path="/astrology-chart" element={<Wrap><AstrologyChart /></Wrap>} />
        <Route path="/chart" element={<Wrap><ChartPage /></Wrap>} />
        <Route path="/chart/report" element={<Wrap><ChartReportPage /></Wrap>} />
        <Route path="/chart/report/print" element={<Wrap><ChartReportPrintPage /></Wrap>} />
        <Route path="/witch" element={<Wrap><WitchProfile /></Wrap>} />
        <Route path="/runes" element={<Wrap><Runes /></Wrap>} />
        <Route path="/tarot" element={<Wrap><Tarot /></Wrap>} />
        <Route path="/courses" element={<Wrap><Courses /></Wrap>} />
        <Route path="/courses/runes" element={<Wrap><RunesCourseIndex /></Wrap>} />
        <Route path="/courses/runes/:slug" element={<Wrap><RuneDeepDive /></Wrap>} />
        <Route path="/courses/tarot" element={<Wrap><TarotCoursePage /></Wrap>} />
        <Route path="/palmistry-101" element={<Wrap><Palmistry101Page /></Wrap>} />
        <Route path="/numerology" element={<Wrap><NumerologyLayout /></Wrap>}>
          <Route path="mini-course" element={<Navigate to="/numerology/study" replace />} />
          <Route path="full-report" element={<Navigate to="/numerology/report" replace />} />
          <Route path="bundle" element={<Navigate to="/numerology" replace />} />
          <Route path="learn" element={<Navigate to="/numerology/study" replace />} />
          <Route path="learn/:slug" element={<RedirectNumerologyLearnSlugToStudy />} />
          <Route path="samples" element={<Navigate to="/numerology/study/samples" replace />} />
          <Route index element={<NumerologyHubPage />} />
          <Route path="study/samples" element={<NumerologySamplesPage />} />
          <Route path="study/:slug" element={<NumerologyStudySlugPage />} />
          <Route path="study" element={<NumerologyLearnLandingPage />} />
          <Route path="report/print" element={<NumerologyReportPrintPage />} />
          <Route path="report" element={<NumerologyReportEnginePage />} />
        </Route>
        <Route
          path="/numerology-report-engine/print"
          element={<Wrap><NumerologyLegacyAccessRedirect to="/numerology/report/print" /></Wrap>}
        />
        <Route
          path="/numerology-report-engine"
          element={<Wrap><NumerologyLegacyAccessRedirect to="/numerology/report" /></Wrap>}
        />
        <Route path="/horoscope" element={<Wrap><Horoscope /></Wrap>} />
        <Route path="/register" element={<Wrap><Register /></Wrap>} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Wrap><Login /></Wrap>} />
        <Route path="/welcome" element={<ProtectedRoute><Wrap><Welcome /></Wrap></ProtectedRoute>} />
        <Route path="/profile-setup" element={<ProtectedRoute><Wrap><ProfileSetup /></Wrap></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><RequireBirthData><Wrap><Dashboard /></Wrap></RequireBirthData></ProtectedRoute>} />
        <Route path="/charts" element={<ProtectedRoute><Wrap><Charts /></Wrap></ProtectedRoute>} />
        <Route path="/charts2" element={<Wrap><Charts2 /></Wrap>} />
        <Route path="/profile" element={<ProtectedRoute><RequireBirthData><Wrap><Profile /></Wrap></RequireBirthData></ProtectedRoute>} />
        <Route path="/birth-data" element={<ProtectedRoute><RequireBirthData><Wrap><BirthData /></Wrap></RequireBirthData></ProtectedRoute>} />
        <Route path="/files" element={<ProtectedRoute><Wrap><Files /></Wrap></ProtectedRoute>} />
        {process.env.NODE_ENV !== 'production' && (
          <Route path="/debug" element={<Wrap><Debug /></Wrap>} />
        )}
        <Route path="*" element={<Wrap><NotFound /></Wrap>} />
      </Routes>
    </div>
  );
}

export default App;
